import { lazy, Suspense } from 'react';

import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { AuthGuard } from '@/shared/components/guards/AuthGuard';
import { IOSRedirectGuard } from '@/shared/components/guards/IOSRedirectGuard';
import { ProfileGuard } from '@/shared/components/guards/ProfileGuard';
import { PublicGuard } from '@/shared/components/guards/PublicGuard';
import { DashboardLayout } from '@/shared/components/layout/DashboardLayout';
import { LegalConsentModal } from '@/shared/components/LegalConsentModal';

const SignInPage = lazy(() =>
  import('@/features/auth/pages/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpOnboardingPage = lazy(() =>
  import('@/features/auth/pages/SignUpOnboardingPage').then((m) => ({
    default: m.SignUpOnboardingPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const PasswordResetPage = lazy(() =>
  import('@/features/auth/pages/PasswordResetPage').then((m) => ({
    default: m.PasswordResetPage,
  })),
);
const ProfileSelectPage = lazy(() =>
  import('@/features/auth/pages/ProfileSelectPage').then((m) => ({
    default: m.ProfileSelectPage,
  })),
);

const AppointmentsPage = lazy(() =>
  import('@/features/appointments/pages/AppointmentsPage').then((m) => ({
    default: m.AppointmentsPage,
  })),
);
const PatientsPage = lazy(() =>
  import('@/features/patients/pages/PatientsPage').then((m) => ({
    default: m.PatientsPage,
  })),
);
const TemplatesPage = lazy(() =>
  import('@/features/templates/pages/TemplatesPage').then((m) => ({
    default: m.TemplatesPage,
  })),
);
const TemplateBuilderPage = lazy(() =>
  import('@/features/templates/pages/TemplateBuilderPage').then((m) => ({
    default: m.TemplateBuilderPage,
  })),
);
const ReferralsPage = lazy(() =>
  import('@/features/referrals/pages/ReferralsPage').then((m) => ({
    default: m.ReferralsPage,
  })),
);
const ReferralDocumentDetailPage = lazy(() =>
  import('@/features/referrals/pages/ReferralDocumentDetailPage').then((m) => ({
    default: m.ReferralDocumentDetailPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const AccountProfilePage = lazy(() =>
  import('@/features/settings/pages/AccountProfilePage').then((m) => ({
    default: m.AccountProfilePage,
  })),
);
const SecurityPage = lazy(() =>
  import('@/features/settings/pages/SecurityPage').then((m) => ({
    default: m.SecurityPage,
  })),
);
const ReleaseNotesPage = lazy(() =>
  import('@/features/settings/pages/ReleaseNotesPage').then((m) => ({
    default: m.ReleaseNotesPage,
  })),
);
const ProfilesPage = lazy(() =>
  import('@/features/profiles/pages/ProfilesPage').then((m) => ({
    default: m.ProfilesPage,
  })),
);
const PatientDetailPage = lazy(() =>
  import('@/features/patients/pages/PatientDetailPage').then((m) => ({
    default: m.PatientDetailPage,
  })),
);
const ChartDetailPage = lazy(() =>
  import('@/features/charts/pages/ChartDetailPage').then((m) => ({
    default: m.ChartDetailPage,
  })),
);
const AccountTerminationPage = lazy(() =>
  import('@/features/auth/pages/AccountTerminationPage').then((m) => ({
    default: m.AccountTerminationPage,
  })),
);
const PricingPage = lazy(() =>
  import('@/features/subscriptions/pages/PricingPage').then((m) => ({
    default: m.PricingPage,
  })),
);
const BillingPage = lazy(() =>
  import('@/features/subscriptions/pages/BillingPage').then((m) => ({
    default: m.BillingPage,
  })),
);
const CheckoutThankYouPage = lazy(() =>
  import('@/features/subscriptions/pages/CheckoutThankYouPage').then((m) => ({
    default: m.CheckoutThankYouPage,
  })),
);
const CheckoutCancelPage = lazy(() =>
  import('@/features/subscriptions/pages/CheckoutCancelPage').then((m) => ({
    default: m.CheckoutCancelPage,
  })),
);
const TermsPage = lazy(() =>
  import('@/features/legal/pages/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const SupportPage = lazy(() =>
  import('@/features/support/pages/SupportPage').then((m) => ({ default: m.SupportPage })),
);
const NewTicketPage = lazy(() =>
  import('@/features/support/pages/NewTicketPage').then((m) => ({ default: m.NewTicketPage })),
);
const TicketDetailPage = lazy(() =>
  import('@/features/support/pages/TicketDetailPage').then((m) => ({
    default: m.TicketDetailPage,
  })),
);
const PrivacyPage = lazy(() =>
  import('@/features/legal/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const ReviewQueuePage = lazy(() =>
  import('@/features/review-queue/pages/ReviewQueuePage').then((m) => ({
    default: m.ReviewQueuePage,
  })),
);
const ExportsPage = lazy(() =>
  import('@/features/exports/pages/ExportsPage').then((m) => ({ default: m.ExportsPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/features/integrations/pages/IntegrationsPage').then((m) => ({
    default: m.IntegrationsPage,
  })),
);

function SmartRedirect() {
  return (
    <PublicGuard>
      <Navigate to="/auth/signin" replace />
    </PublicGuard>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <IOSRedirectGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Legal pages — public */}
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />

            {/* Account termination — no auth required (user may be signed out) */}
            <Route path="/account-termination" element={<AccountTerminationPage />} />

            {/* Subscription pages — auth required */}
            <Route
              path="/subscriptions"
              element={
                <AuthGuard>
                  <PricingPage />
                </AuthGuard>
              }
            />
            <Route
              path="/subscriptions/:billing/:tier"
              element={
                <AuthGuard>
                  <PricingPage />
                </AuthGuard>
              }
            />
            <Route
              path="/subscriptions/pro"
              element={
                <AuthGuard>
                  <PricingPage />
                </AuthGuard>
              }
            />
            <Route
              path="/subscription/thank-you"
              element={
                <AuthGuard>
                  <CheckoutThankYouPage />
                </AuthGuard>
              }
            />
            <Route
              path="/subscriptions/cancel"
              element={
                <AuthGuard>
                  <CheckoutCancelPage />
                </AuthGuard>
              }
            />

            {/* Public routes */}
            <Route
              path="/auth/signin"
              element={
                <PublicGuard>
                  <SignInPage />
                </PublicGuard>
              }
            />
            <Route
              path="/auth/signup"
              element={
                <PublicGuard>
                  <SignUpOnboardingPage />
                </PublicGuard>
              }
            />
            <Route
              path="/auth/password-forgot"
              element={
                <PublicGuard>
                  <ForgotPasswordPage />
                </PublicGuard>
              }
            />
            <Route
              path="/auth/password-reset"
              element={
                <PublicGuard>
                  <PasswordResetPage />
                </PublicGuard>
              }
            />
            <Route
              path="/authAction"
              element={
                <PublicGuard>
                  <PasswordResetPage />
                </PublicGuard>
              }
            />

            {/* Auth-protected routes (non-dashboard) */}
            <Route
              path="/dashboard/additional-info"
              element={<Navigate to="/auth/signup" replace />}
            />
            <Route
              path="/profiles/select"
              element={
                <AuthGuard>
                  <ProfileSelectPage />
                </AuthGuard>
              }
            />

            {/* Dashboard routes with sidebar layout */}
            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <ProfileGuard>
                    <DashboardLayout />
                  </ProfileGuard>
                </AuthGuard>
              }
            >
              <Route index element={<Navigate to="appointments" replace />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="patients" element={<PatientsPage />} />
              <Route path="patients/:patientId" element={<PatientDetailPage />} />
              <Route path="charts/:chartId" element={<ChartDetailPage />} />
              <Route path="review-queue" element={<ReviewQueuePage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="templates/new" element={<TemplateBuilderPage />} />
              <Route path="templates/:id/edit" element={<TemplateBuilderPage />} />
              <Route path="exports" element={<ExportsPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="referrals" element={<ReferralsPage />} />
              <Route path="documents" element={<ReferralsPage />} />
              <Route path="documents/:docId" element={<ReferralDocumentDetailPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="profile" element={<AccountProfilePage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="release-notes" element={<ReleaseNotesPage />} />
              <Route path="profiles" element={<ProfilesPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="support/new" element={<NewTicketPage />} />
              <Route path="support/:ticketId" element={<TicketDetailPage />} />
            </Route>

            {/* Preview routes — no auth required, for design review */}
            <Route path="/preview" element={<DashboardLayout />}>
              <Route index element={<Navigate to="appointments" replace />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="patients" element={<PatientsPage />} />
              <Route path="patients/:patientId" element={<PatientDetailPage />} />
              <Route path="charts/:chartId" element={<ChartDetailPage />} />
              <Route path="profiles" element={<ProfilesPage />} />
            </Route>

            {/* Redirects */}
            <Route path="/getStartedToday" element={<Navigate to="/auth/signup" replace />} />
            <Route
              path="/waitList"
              element={<Navigate to="/auth/signup?waitlist=true" replace />}
            />
            <Route path="/" element={<SmartRedirect />} />
            <Route path="*" element={<SmartRedirect />} />
          </Routes>
        </Suspense>
      </IOSRedirectGuard>
      <LegalConsentModal />
    </BrowserRouter>
  );
}
